import React, { useEffect, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useDebounce } from "../../../hooks/useDebounce";
import { useLazyCustomers, useLazyCustomer } from "../hooks";

interface ICustomerSelectSearch {
  form: UseFormReturn<any, any>;
  name?: string;
  label?: string;
  placeholder?: string;
}

export const CustomerSelectSearch: React.FC<ICustomerSelectSearch> = ({
  form,
  name = "customerId",
  label = "Client",
  placeholder = "Rechercher un client...",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpened, setIsOpened] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedName, setSelectedName] = useState("");
  const debouncedSearch = useDebounce(search, 300);

  const [searchFn, { data, loading }] = useLazyCustomers();
  const [lazyCustomer] = useLazyCustomer();

  const customerId = form.watch(name);

  useEffect(() => {
    if (!customerId) {
      setSelectedName("");
      return;
    }
    if (!selectedName) {
      lazyCustomer({ variables: { id: +customerId } }).then(({ data: customerData }) => {
        if (customerData?.customer?.result) {
          setSelectedName(customerData.customer.result.name);
        }
      });
    }
  }, [customerId]);

  useEffect(() => {
    if (debouncedSearch && debouncedSearch.length >= 2) {
      searchFn({ variables: { where: { search: debouncedSearch } } });
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpened(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const clearSelection = () => {
    form.setValue(name, "");
    setSelectedName("");
    setSearch("");
  };

  if (customerId && selectedName) {
    return (
      <div className="flex flex-col w-full">
        <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">{label}</label>
        <div className="flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-zinc-700 rounded-md text-sm bg-gray-50 dark:bg-zinc-900 text-gray-900 dark:text-zinc-100">
          <span className="truncate">{selectedName}</span>
          <button
            type="button"
            onClick={clearSelection}
            className="text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300 ml-2"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full relative" ref={containerRef}>
      <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">{label}</label>
      <input
        className="w-full px-3 py-2 border rounded-md text-sm bg-white dark:bg-surface-dark-alt text-gray-900 dark:text-zinc-100 border-gray-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-primary-500 focus:ring-primary-500 transition-colors duration-200"
        placeholder={placeholder}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setIsOpened(true);
        }}
        onFocus={() => search.length >= 2 && setIsOpened(true)}
        autoComplete="off"
      />

      {isOpened && search.length >= 2 && (
        <div className="absolute z-10 top-full mt-1 w-full bg-white dark:bg-surface-dark-alt border border-gray-200 dark:border-zinc-800 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {loading && (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-400">Recherche...</div>
          )}
          {!loading && data?.customers?.results?.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-zinc-400">Aucun résultat</div>
          )}
          {data?.customers?.results?.map((customer) => (
            <div
              key={`customer-option-${customer.id}`}
              className="px-3 py-2 text-sm text-gray-900 dark:text-zinc-100 cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800"
              onClick={() => {
                form.setValue(name, customer.id);
                setSelectedName(customer.name);
                setSearch("");
                setIsOpened(false);
              }}
            >
              {customer.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
